import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";
import { expandSearchTerms } from "@/lib/searchSynonyms";
import { queryKeys } from "@/lib/queryKeys";
import { assetUpdateMayAffectFilteredLists, patchAssetEverywhere } from "@/lib/cachePatch";
import { track } from "@/lib/analytics";

export type AssetTag = {
  tag_id: string;
  user_tags: { id: string; name: string; color: string } | null;
};

export type Asset = Tables<"assets"> & {
  locations?: Tables<"locations"> | null;
  asset_documents?: Tables<"asset_documents">[];
  asset_tags?: AssetTag[];
};

export function useAssets(filters?: { search?: string; locationId?: string; warrantyStatus?: string }) {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.assets.list(user?.id, filters),
    queryFn: async () => {
      let query = supabase
        .from("assets")
        .select("*, locations(*), asset_documents!asset_documents_asset_id_fkey(*), asset_tags(tag_id, user_tags(id, name, color))")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (filters?.search) {
        const terms = expandSearchTerms(filters.search);
        if (terms.length > 0) {
          const orClauses = terms.flatMap((t) => [
            `name.ilike.%${t}%`,
            `brand.ilike.%${t}%`,
            `model.ilike.%${t}%`,
            `category.ilike.%${t}%`,
          ]);
          query = query.or(orClauses.join(","));
        }
      }
      if (filters?.locationId) {
        query = query.eq("location_id", filters.locationId);
      }

      const { data, error } = await query;
      if (error) throw error;

      let assets = data as Asset[];

      if (filters?.warrantyStatus) {
        const now = new Date().toISOString().split("T")[0];
        if (filters.warrantyStatus === "active") {
          assets = assets.filter((a) => a.warranty_expiry && a.warranty_expiry >= now);
        } else if (filters.warrantyStatus === "expired") {
          assets = assets.filter((a) => a.warranty_expiry && a.warranty_expiry < now);
        } else if (filters.warrantyStatus === "expiring_soon") {
          const thirtyDays = new Date();
          thirtyDays.setDate(thirtyDays.getDate() + 30);
          const soon = thirtyDays.toISOString().split("T")[0];
          assets = assets.filter((a) => a.warranty_expiry && a.warranty_expiry >= now && a.warranty_expiry <= soon);
        }
      }

      return assets;
    },
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useAsset(id: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.asset.detail(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assets")
        .select("*, locations(*), asset_documents!asset_documents_asset_id_fkey(*), asset_tags(tag_id, user_tags(id, name, color))")
        .eq("id", id)
        .eq("user_id", user!.id)
        .single();
      if (error) throw error;
      return data as Asset;
    },
    enabled: !!user && !!id,
    staleTime: 60_000,
    placeholderData: () => {
      // Seed from any cached assets list query
      const queries = queryClient.getQueriesData<Asset[]>({ queryKey: queryKeys.assets.root });
      for (const [, assets] of queries) {
        const match = assets?.find((a) => a.id === id);
        if (match) return match;
      }
      return undefined;
    },
  });
}

export function useCreateAsset() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (asset: Omit<TablesInsert<"assets">, "user_id">) => {
      const { data, error } = await supabase
        .from("assets")
        .insert({ ...asset, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      track("asset_created");
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.root });
    },
  });
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<TablesInsert<"assets">>) => {
      const { data, error } = await supabase
        .from("assets")
        .update(updates)
        .eq("id", id)
        .eq("user_id", user!.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      track("asset_updated");
      // Patch the detail cache and list rows for the common case.
      patchAssetEverywhere(queryClient, variables.id, data as Partial<Asset>);

      // Some fields affect whether an asset belongs in filtered lists.
      // Refetch those caches so rows are added/removed correctly.
      if (assetUpdateMayAffectFilteredLists(variables)) {
        queryClient.invalidateQueries({ queryKey: queryKeys.assets.root });
      }
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("assets").delete().eq("id", id).eq("user_id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      track("asset_deleted");
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.root });
    },
  });
}

export function useDeleteAssets() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("assets").delete().in("id", ids).eq("user_id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      track("asset_deleted");
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.root });
    },
  });
}
